import PropTypes from 'prop-types'
import { memo, useEffect, useMemo } from 'react'
import Button from 'components/core/Button/Button'
import useDelayedActiveState from 'lib/react/hooks/useDelayedActiveState'
import conditionalProperties from 'lib/util/conditionalProperties'
import noop from 'lib/util/noop'
import styles from './ButtonScrollPrompt.module.scss'

let repeatShowDelay = 0

/**
 * The `ButtonScrollPrompt`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function ButtonScrollPrompt (props) {
  const { className, delayHide, delayShow, direction, isActive, onClick } =
    props
  const shouldShow = useDelayedActiveState(isActive, delayShow, delayHide)
  const isRender = useDelayedActiveState(
    shouldShow,
    repeatShowDelay + 0.01,
    0.25
  )
  const isShown = useDelayedActiveState(shouldShow, repeatShowDelay + 0.03, 0)
  const attributes = useMemo(
    () => ({
      className: `${styles.scrollPrompt} ${className}`.trimEnd(),
      ...conditionalProperties([[isShown, { 'data-a': '' }]])
    }),
    [isShown]
  )
  const icon = useMemo(
    () => (direction === 'vertical' ? 'arrow_down' : 'arrow_right'),
    [direction]
  )

  /**
   * Effect: When `isRender` increment the `repeatShowDelay` to
   * gradually increase the time to prompt users to scroll.
   */
  useEffect(() => {
    if (!isRender) {
      return
    }

    // Gradually increase the show delay
    repeatShowDelay += 0.5
    repeatShowDelay = Math.min(repeatShowDelay, 5)
  }, [isRender])

  return (
    <>
      {isRender && (
        <div className={`${styles.scrollPrompt} ${className}`} {...attributes}>
          <div className={styles.scrollPromptDisplay}>
            <Button
              size="x-small"
              useShadow={true}
              color="e"
              icon={icon}
              side="right"
              iconAnimation={direction}
              onClick={onClick}
            >
              Scroll for more
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

/** @type {object} */
ButtonScrollPrompt.propTypes = {
  className: PropTypes.string,
  isActive: PropTypes.bool,
  delayShow: PropTypes.number,
  delayHide: PropTypes.number,
  direction: PropTypes.string,
  onClick: PropTypes.func
}

/** @type {object} */
ButtonScrollPrompt.defaultProps = {
  className: '',
  isActive: false,
  delayShow: 1,
  delayHide: 0,
  direction: 'vertical',
  onClick: noop
}

// Memoize
export default memo(ButtonScrollPrompt)
